import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Mark } from "../components/Shell";
import { Button, ErrorNotice, Field, Input } from "../components/ui";
import { ApiClient, ApiError, authApi, defaultBaseUrl } from "../lib/api";
import { docsHome, marketingHome } from "../lib/site";
import { Link, navigate } from "../lib/router";
import { MIN_PASSWORD, classifyCredential, useSession } from "../lib/session";
import type { LoginChallenge } from "../lib/types";

type Mode = "signin" | "signup";

export function Login({ mode = "signin" }: { mode?: Mode }) {
  const { signInWithPassword, register, signIn, signedOutReason } = useSession();
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [useToken, setUseToken] = useState(false);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const kind = classifyCredential(token);
  const signup = mode === "signup";

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (signup) {
        await register(name, email, password);
        navigate("/dev/games?welcome=1", { replace: true });
      } else {
        const r = await signInWithPassword(email, password);
        if ("challenge" in r) {
          setChallenge(r.challenge);
          return;
        }
        navigate("/dev", { replace: true });
      }
    });
  };

  const submitToken = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const s = await signIn(token, defaultBaseUrl());
      navigate(s.role === "admin" ? "/admin" : "/dev", { replace: true });
    });
  };

  const switchMode = () => {
    setError(null);
    setPassword("");
  };

  if (challenge) {
    return (
      <AuthLayout>
        <CodeStep challenge={challenge} onChallenge={setChallenge} onBack={() => { setChallenge(null); setPassword(""); setError(null); }} />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
        {!useToken ? (
          <form onSubmit={submitPassword} aria-labelledby="signin-title">
            <div>
              <h2 id="signin-title">{signup ? "Create your developer account" : "Sign in"}</h2>
              <p className="muted" style={{ marginTop: 6 }}>
                {signup
                  ? "Free sandbox access. You'll create your first game right after."
                  : "Welcome back. Sign in to manage your games and sandbox."}
              </p>
            </div>
            {signedOutReason && <div className="notice warn">{signedOutReason}</div>}
            {signup && (
              <Field label="Studio or your name">
                <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="organization" maxLength={120} required autoFocus />
              </Field>
            )}
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" maxLength={254} required autoFocus={!signup} />
            </Field>
            <Field label="Password" hint={signup ? `At least ${MIN_PASSWORD} characters.` : undefined}>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={signup ? "new-password" : "current-password"}
                minLength={signup ? MIN_PASSWORD : undefined}
                maxLength={128}
                required
              />
            </Field>
            <ErrorNotice error={error} />
            <Button type="submit" variant="primary" busy={busy}>
              {signup ? "Create account" : "Sign in"}
            </Button>
            <p className="muted small">
              {signup ? "Already have an account? " : "New here? "}
              <Link to={signup ? "/login" : "/signup"} replace onClick={switchMode}>
                {signup ? "Sign in" : "Create a developer account"}
              </Link>
            </p>
            {signup && (
              <p className="muted small">
                For in-game money only. Game Banking Engine simulates banking for game currencies; it holds no real funds
                and isn't a regulated financial service.
              </p>
            )}
            {!signup && (
              <p className="muted small">
                <button type="button" className="btn ghost small" onClick={() => { setUseToken(true); setError(null); }}>
                  Sign in with a credential or the admin token
                </button>
              </p>
            )}
            <p className="muted small">You stay signed in in this browser tab until you close it or after 3 hours idle.</p>
          </form>
        ) : (
          <form onSubmit={submitToken} aria-labelledby="token-title">
            <div>
              <h2 id="token-title">Sign in with a credential</h2>
              <p className="muted" style={{ marginTop: 6 }}>
                For a developer credential (gbe_dev_…) or the platform admin token.
              </p>
            </div>
            {signedOutReason && <div className="notice warn">{signedOutReason}</div>}
            <Field
              label="Credential"
              hint={
                kind === "developer" ? "Developer credential: you'll manage your games and sandbox."
                : kind === "admin" ? "Treated as the platform admin token."
                : kind === "game_key" ? "This is a game API key. Sign in with your email and password instead."
                : "A developer credential starts with gbe_dev_."
              }
            >
              <Input
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="gbe_dev_…"
                aria-invalid={kind === "game_key" || undefined}
                required
                autoFocus
              />
            </Field>
            <ErrorNotice error={error} />
            <Button type="submit" variant="primary" busy={busy} disabled={!token.trim()}>
              Sign in
            </Button>
            <p className="muted small">
              <button type="button" className="btn ghost small" onClick={() => { setUseToken(false); setError(null); }}>
                Back to email sign-in
              </button>
            </p>
            <p className="muted small">You stay signed in in this browser tab until you close it or after 3 hours idle.</p>
          </form>
        )}
    </AuthLayout>
  );
}

/** The two-column sign-in layout: the brand panel on the left, the form on the right. */
/** The second sign-in step: the 6-digit code we emailed. */
function CodeStep({ challenge, onChallenge, onBack }: {
  challenge: LoginChallenge;
  onChallenge: (c: LoginChallenge) => void;
  onBack: () => void;
}) {
  const { verifyLoginCode } = useSession();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState(false);
  const [now, setNow] = useState(Date.now());
  const expired = error instanceof ApiError && error.code === "login_code_expired";
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);
  const wait = Math.max(0, Math.ceil((Date.parse(challenge.resend_at) - now) / 1000));

  const submit = async (value: string) => {
    if (busy || value.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      await verifyLoginCode(challenge.id, value);
      navigate("/dev", { replace: true });
    } catch (err) {
      setError(err);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError(null);
    try {
      const r = await authApi(new ApiClient(defaultBaseUrl(), "")).resendLoginCode(challenge.id);
      onChallenge(r.challenge);
      setSent(true);
      setCode("");
    } catch (err) {
      setError(err);
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); void submit(code); }} aria-labelledby="code-title">
      <div>
        <h2 id="code-title">Check your email</h2>
        <p className="muted" style={{ marginTop: 6 }}>
          We sent a 6-digit sign-in code to <strong>{challenge.sent_to}</strong>. It expires in 10 minutes.
        </p>
      </div>
      {sent && !error && <div className="notice" role="status">A new code is on its way. Only the newest one works.</div>}
      <Field label="Sign-in code">
        <Input
          className="code-input"
          value={code}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(v);
            if (v.length === 6) void submit(v);
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          placeholder="000000"
          aria-describedby="code-help"
          disabled={expired}
          autoFocus
          required
        />
      </Field>
      <ErrorNotice error={error} />
      {error instanceof ApiError && typeof error.details?.attempts_left === "number" && (
        <p className="muted small" style={{ marginTop: -6 }}>
          {error.details.attempts_left} {error.details.attempts_left === 1 ? "try" : "tries"} left.
        </p>
      )}
      {expired ? (
        <Button variant="primary" onClick={onBack}>Sign in again</Button>
      ) : (
        <Button type="submit" variant="primary" busy={busy} disabled={code.length !== 6}>Continue</Button>
      )}
      <p className="muted small" id="code-help">
        Can't find it? Check spam, or{" "}
        <button type="button" className="btn ghost small" onClick={resend} disabled={wait > 0 || resending || expired}>
          {wait > 0 ? `send a new code in ${wait} s` : "send a new code"}
        </button>
      </p>
      <p className="muted small">
        <button type="button" className="btn ghost small" onClick={onBack}>Use a different account</button>
      </p>
    </form>
  );
}

export function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="login">
      <section className="login-art" aria-hidden="false">
        <a className="login-brand" href={marketingHome()}>
          <Mark size={34} />
          <span>Game Banking Engine</span>
        </a>
        <div>
          <h1>Run the bank behind your game.</h1>
          <p>
            Configure branded banks, issue API keys and try every money movement in the sandbox before a single player
            touches it. Every posting is double-entry, so the books always balance.
          </p>
          <a className="login-docs" href={docsHome()}>Read the developer guide →</a>
        </div>
        <div className="ticket" aria-label="Example transfer slip">
          <div className="t-row"><span>Session</span><span>058261013201613393</span></div>
          <div className="t-row"><span>Game time</span><span>01 Jun 2040 09:00</span></div>
          <div className="t-rule" />
          <div className="t-row"><span>Dr  Ada Obi  0853191403</span><span>₦3,200.00</span></div>
          <div className="t-row"><span>Cr  Tunde Bello  5915945174</span><span>₦3,200.00</span></div>
          <div className="t-rule" />
          <div className="t-row"><span>Debits equal credits</span><span>Posted</span></div>
        </div>
      </section>
      <section className="login-form">{children}</section>
    </div>
  );
}
